/**
 * Link motion tokens.
 *
 * All durations and easings used throughout the Rider + Driver + Admin
 * apps. Importing from here guarantees every animation in the product
 * feels like it belongs to the same family.
 *
 * Aligned with the Master Upgrade Spec (Phase A · A4):
 *   • duration-fast = 150 ms
 *   • duration-base = 250 ms
 *   • duration-slow = 400 ms
 *   • entrance ease = cubic-bezier(0.16, 1, 0.3, 1)
 *   • interactive spring = gentle ~1.05 overshoot
 * Nothing over 400 ms except the Phase B route-draw animation.
 *
 * `prefers-reduced-motion` is honoured globally via <MotionConfig
 * reducedMotion="user"> wrapped in <App>; per-component code should
 * still design animations that read as CROSSFADES when reduced.
 */

export const DURATION = {
  fast:   0.15,   // hover, ripple, small fade
  normal: 0.25,   // most state changes, cross-fades, step slides
  slow:   0.40,   // morphs, sheets, page transitions
  // NOTE: `large` is preserved as an alias for existing call sites
  // that already spelled `DURATION.large`. New code should use `slow`.
  large:  0.40,
  xlarge: 0.60,   // first-load reveal sequences only
};

export const EASE = {
  // Spec-mandated ease-out for entrances — the "gentle landing" curve
  // Apple uses on sheet slides. Slightly softer than 0.22,1,0.36,1.
  out:      [0.16, 1, 0.3, 1],
  inOut:    [0.65, 0, 0.35, 1],
  // Gentle spring overshoot (~1.05) for interactive elements — day pill
  // toggle, primary buttons, feedback confirmations.
  spring:   [0.34, 1.35, 0.5, 1],
  exit:     [0.4, 0, 1, 1],
};

export const SPRING = {
  // Slide-up bottom sheets — playful, physical.
  sheet:   { type: "spring", stiffness: 320, damping: 32, mass: 0.9 },
  // Tight, snappy — for the morphing pill ↔ drawer transition.
  // Duration-based so open+close feel symmetric.
  morph:   { duration: 0.40, ease: [0.16, 1, 0.3, 1] },
  // Gentle, used for floating chrome reveals.
  chrome:  { type: "spring", stiffness: 220, damping: 26 },
  // Interactive pop — day pill tap, small CTA feedback (1.0 → 1.08 → 1.0).
  pop:     { type: "spring", stiffness: 400, damping: 15, mass: 0.5 },
};

export const VARIANTS = {
  fadeIn:      { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } },
  fadeSlideUp: {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    exit:    { opacity: 0, y: 8 },
  },
  fadeScale: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit:    { opacity: 0, scale: 0.95 },
  },
  sheetUp: {
    initial: { y: "100%" },
    animate: { y: 0 },
    exit:    { y: "100%" },
  },
  page: {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit:    { opacity: 0, y: -6 },
  },
  // Directional step transitions for the booking flow.
  // Forward: current step slides out left, next slides in from right.
  // Backward: reverse. Framer's `custom` prop drives the direction:
  //     <AnimatePresence custom={dir}>
  //       <motion.div variants={VARIANTS.stepSlide} custom={dir} ... />
  stepSlide: {
    initial: (dir = 1) => ({ opacity: 0, x: dir >= 0 ? 40 : -40 }),
    animate: { opacity: 1, x: 0, transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] } },
    exit:    (dir = 1) => ({ opacity: 0, x: dir >= 0 ? -40 : 40, transition: { duration: 0.20, ease: [0.4, 0, 1, 1] } }),
  },
  staggerChildren: { animate: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } } },
};

// Tailwind / CSS strings that mirror the same tokens. Use these on any
// component that uses CSS transitions instead of framer-motion.
export const CSS_EASE_OUT = "cubic-bezier(0.16, 1, 0.3, 1)";
export const CSS_EASE_SPRING = "cubic-bezier(0.34, 1.35, 0.5, 1)";
